import { BigIntAmount } from '@ui/components/BigIntAmount/BigIntAmount';
import { X as XIcon } from '@ui/components/icons';
import { useJoinTeam } from '@ui/features/GenesisAirdrop/useJoinTeam';
import { useRouter } from '@ui/lib/i18n';
import { Button } from '@ui/lib/park/ui/button';
import { Dialog } from '@ui/lib/park/ui/dialog';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import { useTeamInfoByCode } from '@ui/services/contract/hooks/GenesisAirdrop/useTeamInfoByCode';
import { Flex, HStack, VStack } from 'styled-system/jsx';

import { useReferralModalStore } from './store/useReferralModalStore';

const removeCodeFromUrl = (router: ReturnType<typeof useRouter>) => {
  const { pathname, query } = router;
  if (!('code' in query)) return;
  const newQuery = { ...query } as Record<string, any>;
  delete newQuery.code;
  router.replace({ pathname, query: newQuery }, undefined, { shallow: true });
};

export const ReferralCodeModal = () => {
  const router = useRouter();

  const isOpen = useReferralModalStore((s) => s.isOpen);
  const kind = useReferralModalStore((s) => s.kind);
  const code = useReferralModalStore((s) => s.code);
  const close = useReferralModalStore((s) => s.close);

  const handleCloseAndClear = () => {
    close();
    removeCodeFromUrl(router);
  };

  const handleConfirmUse = async () => {
    joinTeam();
  };

  const { writeContract: joinTeam, isPending } = useJoinTeam({
    code: code ?? '',
    onSettled: () => {
      handleCloseAndClear();
    },
  });

  const { data: teamInfo, isLoading: isTeamInfoLoading } = useTeamInfoByCode(
    kind === 'confirm_use' ? code ?? undefined : undefined,
  );

  const title =
    kind === 'connect'
      ? 'Connect Wallet'
      : kind === 'own_leader'
      ? 'Cannot Use Your Own Code'
      : kind === 'already_member'
      ? 'Already Joined a Team'
      : 'Use Referral Code?';

  // Inline highlighted code styling will be used directly in the description below

  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      modal={false}
      open={isOpen}
      type="desktopModal_mobileDrawer"
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content
          p={2}
          color="v2.text.primary"
          minW="420px"
          maxW="440px"
        >
          <Dialog.Title asChild>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              p="16px"
            >
              <Text textStyle="headings.large">{title}</Text>
              <Dialog.CloseTrigger
                boxShadow="none"
                bg="none"
                onClick={() =>
                  kind === 'connect' ? close() : handleCloseAndClear()
                }
              >
                <Icon w="20px" h="20px">
                  <XIcon />
                </Icon>
              </Dialog.CloseTrigger>
            </Flex>
          </Dialog.Title>

          <Dialog.Description
            p="16px"
            pt={0}
            display="flex"
            flexDir="column"
            gap={4}
          >
            <VStack alignItems="flex-start" gap={3}>
              <Text textStyle="text.default" color="v2.text.secondary">
                {kind === 'connect' ? (
                  <>
                    Connect your wallet to use referral code{' '}
                    <Text as="span" color="v2.long.default" fontWeight="bold">
                      {code ?? ''}
                    </Text>
                    .
                  </>
                ) : kind === 'own_leader' ? (
                  <>
                    The code{' '}
                    <Text as="span" color="v2.long.default" fontWeight="bold">
                      {code ?? ''}
                    </Text>{' '}
                    was created by you and cannot be used.
                  </>
                ) : kind === 'already_member' ? (
                  <>
                    You have already joined a team. You cannot use code{' '}
                    <Text as="span" color="v2.long.default" fontWeight="bold">
                      {code ?? ''}
                    </Text>
                    .
                  </>
                ) : (
                  <>
                    Do you want to use referral code{' '}
                    <Text as="span" color="v2.long.default" fontWeight="bold">
                      {code ?? ''}
                    </Text>
                    ?
                  </>
                )}
              </Text>

              {kind === 'confirm_use' && (
                <VStack alignItems="flex-start" gap={1}>
                  {isTeamInfoLoading ? (
                    <Text textStyle="text.default" color="v2.text.muted">
                      Loading team info...
                    </Text>
                  ) : teamInfo ? (
                    <>
                      <Text textStyle="text.default">
                        Team:{' '}
                        <Text as="span" fontWeight="bold">
                          {teamInfo.teamName}
                        </Text>
                      </Text>
                      <Text textStyle="text.default">
                        Members: {teamInfo.teamMemberCount}
                      </Text>
                      <Text textStyle="text.default">
                        Total General Points: {teamInfo.totalGeneralPoints}
                      </Text>
                      <Text textStyle="text.default">
                        Total High-Leverage Points: {teamInfo.totalDegenPoints}
                      </Text>
                      <Text textStyle="text.default">
                        Latest Activity Epoch:{' '}
                        {teamInfo.latestEpochTrading.epoch}
                      </Text>
                      <HStack textStyle="text.default">
                        Latest Epoch Volume:
                        <BigIntAmount
                          textStyle="text.small"
                          opts={{
                            decimals: 18,
                            displayDecimals: 2,
                          }}
                          value={BigInt(teamInfo.latestEpochTrading.volumeUSD)}
                        />
                      </HStack>
                    </>
                  ) : (
                    <Text textStyle="text.default" color="v2.short.default">
                      Team not found for this code.
                    </Text>
                  )}
                </VStack>
              )}
            </VStack>

            <HStack gap={3} mt={2} justifyContent="flex-end">
              {kind === 'connect' ? (
                <>
                  <Button variant="secondary" onClick={() => close()}>
                    Later
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => {
                      // connectOrCreateWallet();
                      // Do not clear code here; follow-up flow will handle after connection
                      close();
                    }}
                  >
                    Connect
                  </Button>
                </>
              ) : kind === 'confirm_use' ? (
                <>
                  <Button variant="secondary" onClick={handleCloseAndClear}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleConfirmUse}
                    loading={isPending}
                  >
                    Confirm
                  </Button>
                </>
              ) : (
                <Button variant="primary" onClick={handleCloseAndClear}>
                  OK
                </Button>
              )}
            </HStack>
          </Dialog.Description>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};
